import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Modal, Box, IconButton } from '@mui/material';
import { FaTimes } from 'react-icons/fa';
import BtnLoader from 'Components/Common/Loader/BtnLoader';

const style = {
    position: 'absolute',
    maxHeight: '84%',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 600,
    bgcolor: 'background.paper',
    border: 'none',
    borderRadius: 8,
    boxShadow: 24,
    p: 4,
    overflow: 'auto',
    scrollbarWidth: 'none',
};

const PromoEditPopup = ({ editPromoModal, handleEditModalClose, PromoDetails, fetchPromos }) => {
    const [formData, setFormData] = useState({});
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (PromoDetails) {
            setFormData({ ...PromoDetails }); // Pre-fill the form with existing data
        }
    }, [PromoDetails]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setIsLoading(true);

        try {
            const config = {
                method: 'put',
                maxBodyLength: Infinity,
                url: `${process.env.REACT_APP_API_URL}/promocode/update-promo-codes/${PromoDetails?._id}`,
                headers: {
                    'Content-Type': 'application/json',
                },
                data: JSON.stringify(formData),
            };

            const response = await axios.request(config);
            setSuccess('Promo code updated successfully!');
            fetchPromos()
            console.log("response data", response?.data)
            handleEditModalClose()
            setSuccess('')
            setIsLoading(false);
        } catch (err) {
            setError(err?.response?.data?.message || 'An error occurred. Please try again.');
            setIsLoading(false);
        }
    };

    return (
        <Modal
            open={editPromoModal}
            onClose={handleEditModalClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box sx={style}>
                <IconButton
                    aria-label="close"
                    onClick={handleEditModalClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: '#c51369',
                    }}
                >
                    <FaTimes />
                </IconButton>
                <div className="mx-auto">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <h2 className="text-2xl font-bold mb-6 text-center text-navy-700">Edit Promo Code</h2>

                        {error && <p className="text-red-500 text-center mb-4">{error}</p>}
                        {success && <p className="text-green-500 text-center mb-4">{success}</p>}

                        {/* Promo Code Form Fields */}
                        <div className="space-y-4">
                            {/* Promo Code and Promo Type */}
                            <div className="flex space-x-4">
                                <div className="flex-1">
                                    <label htmlFor="promoCode" className="block tracking-wide text-lg mb-2">Promo Code</label>
                                    <input
                                        type="text"
                                        id="promoCode"
                                        name="promoCode"
                                        value={formData.promoCode || ''}
                                        onChange={handleChange}
                                        className="w-full p-2 border rounded-md"
                                        required
                                    />
                                </div>
                                <div className="flex-1">
                                    <label htmlFor="promoType" className="block tracking-wide text-lg mb-2">Promo Type</label>
                                    <select
                                        id="promoType"
                                        name="promoType"
                                        value={formData.promoType || ''}
                                        onChange={handleChange}
                                        className="w-full p-2 border rounded-md"
                                        required
                                    >
                                        <option value="">Select</option>
                                        <option value="seasonal">Seasonal</option>
                                        <option value="hour-basis">Hour Basis</option>
                                        <option value="special">Special</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex space-x-4">
                                <div className="flex-1">
                                    <label htmlFor="discountAmount" className="block tracking-wide text-lg mb-2">Discount Amount</label>
                                    <input
                                        type="number"
                                        id="discountAmount"
                                        name="discountAmount"
                                        value={formData.discountAmount || ''}
                                        onChange={handleChange}
                                        className="w-full p-2 border rounded-md"
                                        required
                                    />
                                </div>
                                <div className="flex-1">
                                    <label htmlFor="discountPercentage" className="block tracking-wide text-lg mb-2">Discount Percentage</label>
                                    <input
                                        type="number"
                                        id="discountPercentage"
                                        name="discountPercentage"
                                        value={formData.discountPercentage || ''}
                                        onChange={handleChange}
                                        className="w-full p-2 border rounded-md"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="flex space-x-4">
                                <div className="flex-1">
                                    <label htmlFor="usageLimit" className="block tracking-wide text-lg mb-2">Usage Limit</label>
                                    <input
                                        type="number"
                                        id="usageLimit"
                                        name="usageLimit"
                                        value={formData.usageLimit || ''}
                                        onChange={handleChange}
                                        className="w-full p-2 border rounded-md"
                                    />
                                </div>
                                <div className="flex-1">
                                    <label htmlFor="usageCount" className="block tracking-wide text-lg mb-2">Usage Count</label>
                                    <input
                                        type="number"
                                        id="usageCount"
                                        name="usageCount"
                                        value={formData.usageCount || ''}
                                        onChange={handleChange}
                                        className="w-full p-2 border rounded-md"
                                    />
                                </div>
                            </div>

                            <div className="flex space-x-4">
                                <div className="flex-1">
                                    <label htmlFor="minPurchaseAmount" className="block tracking-wide text-lg mb-2">Min Purchase Amount</label>
                                    <input
                                        type="number"
                                        id="minPurchaseAmount"
                                        name="minPurchaseAmount"
                                        value={formData.minPurchaseAmount || ''}
                                        onChange={handleChange}
                                        className="w-full p-2 border rounded-md"
                                    />
                                </div>
                                <div className="flex-1">
                                    <label htmlFor="maxDiscountAmount" className="block tracking-wide text-lg mb-2">Max Discount Amount</label>
                                    <input
                                        type="number"
                                        id="maxDiscountAmount"
                                        name="maxDiscountAmount"
                                        value={formData.maxDiscountAmount || ''}
                                        onChange={handleChange}
                                        className="w-full p-2 border rounded-md"
                                    />
                                </div>
                            </div>

                            <div className="flex space-x-4">
                                <div className="flex-1">
                                    <label htmlFor="applicableTo" className="block tracking-wide text-lg mb-2">Applicable To</label>
                                    <select
                                        id="applicableTo"
                                        name="applicableTo"
                                        value={formData.applicableTo || ''}
                                        onChange={handleChange}
                                        className="w-full p-2 border rounded-md"
                                    >
                                        <option value="">Select</option>
                                        <option value="all">All</option>
                                        <option value="specific">Specific</option>
                                        <option value="specific-category">Specific Category</option>
                                    </select>
                                </div>
                                <div className="flex-1">
                                    <label htmlFor="status" className="block tracking-wide text-lg mb-2">Status</label>
                                    <select
                                        id="status"
                                        name="status"
                                        value={formData.status || ''}
                                        onChange={handleChange}
                                        className="w-full p-2 border rounded-md"
                                    >
                                        <option value="">Select</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                        <option value="expired">Expired</option>
                                    </select>
                                </div>
                            </div>

                            {/* Other form fields */}
                            {/* (Include all other fields like description, discountAmount, dates, etc.) */}

                            <div className="mt-6">
                                {isLoading ? (
                                    <button
                                        className="w-full flex justify-center items-center bg-[#c51369] p-3 rounded-md text-white font-medium"
                                        disabled
                                    >
                                        <BtnLoader />
                                    </button>
                                ) : (
                                    <button
                                        type="submit"
                                        className="w-full bg-[#c51369] text-white p-3 rounded-md hover:bg-[#a10f5b] transition duration-200 font-medium"
                                    >
                                        Save Changes
                                    </button>
                                )}
                            </div>
                        </div>
                    </form>
                </div>
            </Box>
        </Modal>
    );
};

export default PromoEditPopup;
